import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { MessageSquare, BarChart3, Users, FileText, MapPin, ExternalLink, Building2, BookOpen } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';

const DOC_LABEL: Record<string, string> = {
  inventory: 'Inventory', valuation: 'Valuation', 'asset-management-plan': 'Asset Mgmt Plan',
  'financial-report': 'Financial Report', policy: 'Policy', strategy: 'Strategy',
  'case-study': 'Case Study', funding: 'Funding', news: 'News', other: 'Other',
};
const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '');

type Profile = {
  summary: {
    total: number;
    span: { from: string; to: string } | null;
    outputTypes: { documentType: string; count: number }[];
    topTags: { name: string; count: number }[];
  };
  timeline: { id: string; title: string; sourceUrl: string; shortSummary: string; documentType: string | null; publishedAt: string | null; dateCrawled: string; datedByPublication: boolean }[];
};

const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[14px] px-4 py-3 min-w-[130px]">
    <div className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)]">{label}</div>
    <div className="text-[20px] font-semibold text-[color:var(--text-primary)] mt-1">{value}</div>
  </div>
);

export default function CAHome() {
  const { homeEntity, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<Profile | null>(null);
  const [gaps, setGaps] = useState<{ documentType: string; peerCount: number; peerPct: number }[]>([]);
  const [neighbours, setNeighbours] = useState<{ id: string; name: string; region: string | null; contentCount: number }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!homeEntity) { setLoading(false); return; }
    setLoading(true);
    axios.get(`/api/entities/profile?type=org&name=${encodeURIComponent(homeEntity.name)}`)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
    axios.get(`/api/entities/${homeEntity.id}/gaps`).then(r => setGaps(r.data.gaps || [])).catch(() => setGaps([]));
    axios.get(`/api/entities/${homeEntity.id}/neighbours`).then(r => setNeighbours(r.data.neighbours || [])).catch(() => setNeighbours([]));
  }, [homeEntity]);

  if (!homeEntity) {
    return (
      <div className="p-8">
        <h1 className="text-[22px] font-semibold text-[color:var(--text-primary)] mb-2">Welcome{user?.displayName ? `, ${user.displayName}` : ''}</h1>
        <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[16px] p-6 text-[13px] text-[color:var(--text-muted)]">
          Your account isn't linked to a Conservation Authority yet. Ask an administrator to set your home CA, and this page will tailor to your authority. In the meantime you can explore the <Link to="/entities" className="text-[color:var(--accent-blue)]">entity directory</Link> and <Link to="/nam-ai" className="text-[color:var(--accent-blue)]">assistant</Link>.
        </div>
      </div>
    );
  }

  const askAssistant = () => navigate('/nam-ai', { state: { seedPrompt: `Summarize ${homeEntity.name}'s natural asset management work, and how it compares with peer conservation authorities and the broader Canadian NAM landscape.` } });
  const s = data?.summary;

  return (
    <div className="bg-transparent min-h-full p-8">
      {/* Header */}
      <div className="flex items-start gap-4 mb-6 flex-wrap">
        <div className="shrink-0 w-12 h-12 rounded-[12px] bg-[var(--surface)] border border-[var(--border)] flex items-center justify-center text-[color:var(--accent)]">
          <Building2 size={24} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[12px] uppercase tracking-wide text-[color:var(--text-muted)]">Your Conservation Authority</div>
          <h1 className="text-[24px] font-semibold text-[color:var(--text-primary)] m-0">{homeEntity.name}</h1>
          <p className="text-[13px] text-[color:var(--text-muted)] m-0">{homeEntity.province}{homeEntity.region ? ` · ${homeEntity.region}` : ''}</p>
        </div>
        <button onClick={askAssistant} className="inline-flex items-center gap-2 text-[13px] px-3 py-2 rounded-[10px] bg-[var(--accent)] text-white hover:opacity-90">
          <MessageSquare size={15} /> Ask about your CA
        </button>
      </div>

      {/* Quick actions */}
      <div className="grid gap-[10px] mb-6 [grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]">
        <Link to={`/entities/profile?type=org&name=${encodeURIComponent(homeEntity.name)}`} className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[12px] p-3 flex items-center gap-2 hover:border-[color:var(--accent)] text-[13px] text-[color:var(--text-primary)]"><FileText size={16} className="text-[color:var(--accent)]" /> Your full profile</Link>
        <Link to={`/compare?ids=${homeEntity.id}`} className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[12px] p-3 flex items-center gap-2 hover:border-[color:var(--accent)] text-[13px] text-[color:var(--text-primary)]"><Users size={16} className="text-[color:var(--accent)]" /> Compare with peers</Link>
        <Link to="/analytics-dashboard" className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[12px] p-3 flex items-center gap-2 hover:border-[color:var(--accent)] text-[13px] text-[color:var(--text-primary)]"><BarChart3 size={16} className="text-[color:var(--accent)]" /> NAM trends</Link>
        <Link to="/standards" className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[12px] p-3 flex items-center gap-2 hover:border-[color:var(--accent)] text-[13px] text-[color:var(--text-primary)]"><BookOpen size={16} className="text-[color:var(--accent)]" /> Guidelines &amp; standards</Link>
        <Link to="/map" className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[12px] p-3 flex items-center gap-2 hover:border-[color:var(--accent)] text-[13px] text-[color:var(--text-primary)]"><MapPin size={16} className="text-[color:var(--accent)]" /> Map</Link>
      </div>

      {loading && <p className="text-[13px] text-[color:var(--text-muted)]">Loading your CA…</p>}

      {!loading && s && (
        <>
          <div className="flex flex-wrap gap-3 mb-6">
            <Stat label="Items indexed" value={s.total} />
            <Stat label="Output types" value={s.outputTypes.length} />
            <Stat label="Active span" value={s.span ? `${fmt(s.span.from)} – ${fmt(s.span.to)}` : '—'} />
          </div>

          <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[16px] p-5 mb-6">
            <div className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)] mb-2">Outputs your CA has produced</div>
            <div className="flex flex-wrap gap-[6px] mb-4">
              {s.outputTypes.length ? s.outputTypes.map(o => (
                <span key={o.documentType} className="text-[12px] px-2 py-[3px] rounded-full border border-[var(--border)] bg-[var(--surface)] text-[color:var(--accent-blue)]">{DOC_LABEL[o.documentType] || o.documentType} · {o.count}</span>
              )) : <span className="text-[12px] text-[color:var(--text-muted)]">No classified outputs yet.</span>}
            </div>
            {s.topTags.length > 0 && <>
              <div className="text-[11px] uppercase tracking-wide text-[color:var(--text-muted)] mb-2">Top themes</div>
              <div className="flex flex-wrap gap-[6px]">{s.topTags.slice(0, 8).map(t => <span key={t.name} className="text-[12px] px-2 py-[3px] rounded-full border border-[var(--border)] bg-[var(--surface)] text-[color:var(--text-secondary)]">{t.name} · {t.count}</span>)}</div>
            </>}
          </div>

          {gaps.length > 0 && (
            <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[16px] p-5 mb-6">
              <div className="text-[14px] font-semibold text-[color:var(--text-primary)] mb-1">Opportunities to explore</div>
              <p className="text-[12px] text-[color:var(--text-muted)] mb-3">Output types peer conservation authorities have produced that aren’t indexed for your CA yet. <Link to={`/compare?ids=${homeEntity.id}`} className="text-[color:var(--accent-blue)]">Benchmark →</Link></p>
              <div className="flex flex-wrap gap-[6px]">
                {gaps.slice(0, 6).map(g => (
                  <span key={g.documentType} className="text-[12px] px-2 py-[3px] rounded-full border border-[var(--border)] bg-[var(--surface)] text-[color:var(--text-secondary)]">{DOC_LABEL[g.documentType] || g.documentType} · {g.peerCount} peers</span>
                ))}
              </div>
            </div>
          )}

          {neighbours.length > 0 && (
            <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[16px] p-5 mb-6">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[14px] font-semibold text-[color:var(--text-primary)]">Neighbouring conservation authorities</div>
                <Link to={`/compare?ids=${[homeEntity.id, ...neighbours.slice(0, 3).map(n => n.id)].join(',')}`} className="text-[12px] text-[color:var(--accent-blue)]">Compare with neighbours →</Link>
              </div>
              <p className="text-[12px] text-[color:var(--text-muted)] mb-3">Authorities whose watershed jurisdiction borders yours.</p>
              <div className="grid gap-[8px] [grid-template-columns:repeat(auto-fill,minmax(220px,1fr))]">
                {neighbours.map(n => (
                  <Link key={n.id} to={`/entities/profile?type=org&name=${encodeURIComponent(n.name)}`} className="bg-[var(--surface)] border border-[var(--border)] rounded-[10px] p-3 hover:border-[color:var(--accent)]">
                    <div className="text-[13px] text-[color:var(--text-primary)] truncate">{n.name.replace(/ Conservation Authority$/, '')}</div>
                    <div className="text-[11px] text-[color:var(--text-muted)]">{n.contentCount} item{n.contentCount === 1 ? '' : 's'} indexed</div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[16px] p-5">
            <div className="text-[14px] font-semibold text-[color:var(--text-primary)] mb-3">Recent activity</div>
            {(!data || data.timeline.length === 0) ? <p className="text-[13px] text-[color:var(--text-muted)]">No content indexed for your CA yet.</p> : (
              <div className="flex flex-col gap-[10px]">
                {data.timeline.slice(0, 6).map(it => (
                  <div key={it.id} className="border-b border-[var(--border)] pb-2 last:border-0">
                    <div className="text-[11px] text-[color:var(--text-muted)] mb-[2px]">{fmt(it.publishedAt || it.dateCrawled)}{!it.datedByPublication && ' (crawled)'}{it.documentType ? ` · ${DOC_LABEL[it.documentType] || it.documentType}` : ''}</div>
                    <a href={it.sourceUrl} target="_blank" rel="noreferrer" className="text-[14px] font-medium text-[color:var(--text-primary)] hover:text-[color:var(--accent-blue)] inline-flex items-center gap-1">{it.title} <ExternalLink size={12} className="opacity-60" /></a>
                    {it.shortSummary && <p className="text-[13px] text-[color:var(--text-secondary)] mt-1 mb-0">{it.shortSummary}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
